import React from 'react';
import { Users } from 'lucide-react';

import { Card, CardContent } from 'components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import type {
  OrganizationAdminResponseComparisonCell,
  OrganizationAdminResponseQuestion,
  OrganizationAdminUserSummary,
} from 'types';

interface ComparisonTableProps {
  questions: OrganizationAdminResponseQuestion[];
  users: OrganizationAdminUserSummary[];
  responseCellsByKey: Record<string, OrganizationAdminResponseComparisonCell | null>;
  loadingCellKeys: string[];
  exportAction?: React.ReactNode;
}

export const getComparisonCellKey = (userId: number, questionId: number) => `${userId}:${questionId}`;

const LoadingCell: React.FC<{
  userId: number;
  questionId: number;
}> = ({ userId, questionId }) => (
  <div
    className="space-y-2"
    data-testid={`response-comparison-cell-skeleton-${userId}-${questionId}`}
  >
    <div className="h-4 w-40 animate-pulse rounded-full bg-muted" />
    <div className="h-3 w-20 animate-pulse rounded-full bg-muted" />
  </div>
);

export const ComparisonTable: React.FC<ComparisonTableProps> = ({
  questions,
  users,
  responseCellsByKey,
  loadingCellKeys,
  exportAction,
}) => (
  <section aria-labelledby="response-comparison-heading" className="space-y-4">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 id="response-comparison-heading" className="text-2xl font-semibold text-foreground">
          Selected User Comparison
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Rows show selected users; columns show the selected questions.
        </p>
      </div>
      {exportAction ? (
        <div className="shrink-0">
          {exportAction}
        </div>
      ) : null}
    </div>

    {users.length === 0 ? (
      <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
        <CardContent variant="custom" className="px-6 py-12 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-foundation-calm-blue-50">
            <Users className="size-5 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-foreground">Select users to compare</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Aggregate summaries are available without a user selection. Add users to populate this table.
          </p>
        </CardContent>
      </Card>
    ) : (
      <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm ">
        <CardContent variant="custom" className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-max">
            <TableHeader>
              <TableRow>
                <TableHead
                  className="sticky left-0 z-20 min-w-[13rem] bg-white shadow-[1px_0_0_0_theme(colors.border)]"
                  data-testid="response-comparison-user-column-header"
                >
                  User
                </TableHead>
                {questions.map((question) => (
                  <TableHead key={question.id} className="min-w-[16rem]">
                    <span className="line-clamp-2 normal-case tracking-normal text-foreground">
                      {question.text}
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell
                    className="sticky left-0 z-10 min-w-[13rem] bg-white shadow-[1px_0_0_0_theme(colors.border)]"
                    data-testid={`response-comparison-user-cell-${user.id}`}
                  >
                    <p className="font-semibold text-foreground">{user.display_name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </TableCell>
                  {questions.map((question) => {
                    const cellKey = getComparisonCellKey(user.id, question.id);
                    const response = responseCellsByKey[cellKey];
                    const isLoading = loadingCellKeys.includes(cellKey);
                    return (
                      <TableCell key={question.id} className="max-w-[24rem]">
                        {isLoading ? (
                          <LoadingCell userId={user.id} questionId={question.id} />
                        ) : (
                          <>
                            <p className="whitespace-pre-wrap text-sm leading-6 text-foreground">
                              {response?.response_display || '-'}
                            </p>
                            {response?.responded_at ? (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {new Date(response.responded_at).toLocaleDateString()}
                              </p>
                            ) : null}
                          </>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    )}
  </section>
);
